// Production > Production Report (/production/report) — the main production
// report (Stephanie 2026-10-12). Replaces the old "Database" page, the Work
// queue and the Calendar. One row per purchase order with the six columns
// production asked for, in this order: Supplier, PO #, Date placed (when the
// PO was sent to the supplier), Status, Stage, Proof sent, Client, Rep (Order Rep by default,
// switchable to Client Rep; initials), Next action type, Next action date,
// In hands. Every date reads MM/DD/YY (10/07/26).
//
// Reads GET /production/jobs (scope=open, or scope=all to include closed and
// cancelled). Blockers + risk come from the API (services/production-jobs-
// rules.ts) — this page only filters, sorts, labels and exports. Filters live
// in the URL (?stage=&blocker=&rep=&q=&sort=) so a filtered view can be shared;
// the old /production and /production/database links redirect here.
//
// Rush / Firm (Stephanie 2026-10-12): the sales rep ticks them on the order.
// A rush row is inverted (black, white text); a firm row gets a heavy black
// outline; an order can be both. Each also carries a RUSH / FIRM tag by the
// PO #. No colour, so it never competes with the coloured Status.
//
// Status (Stephanie 2026-10-12): production's own call on each PO — OK (sage
// green), Follow Up – Vendor (dusty blue), Follow Up – Rep (plum). Admin and
// production reps change it right in the table. The column appears once Jake
// runs scripts/push-po-production-status.ts.
//
// Proof sent (Stephanie 2026-10-12): see ProofSentCell / ProofSentDialog.
// Supplier is the first column; Stage and Next action are dropdowns
// (Stephanie 2026-10-12): see StageCell / NextActionCell.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, Download, Search, StickyNote, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { PageHeader } from "@/components/ui/PageHeader";
import { FilterTabs, MetricStrip, TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { FreshnessNote, useProductionJobs } from "@/components/production/JobParts";
import { cn } from "@/lib/utils";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import {
  BLOCKER_COPY,
  BLOCKER_ORDER,
  NEXT_ACTION_LABEL,
  daysFromToday,
  poHref,
  poStageLabel,
  sortByUrgency,
  type JobBlocker,
  type ProductionJob,
} from "@/lib/production";
import { MentionTextarea, NoteText } from "@/components/notes/MentionTextarea";
import { csvCell } from "@/lib/csv";

type StageGroup = "in_production" | "not_sent" | "shipping" | "billed" | "closed" | "all";

// "In Production" opens first: everything active — not "Not sent", not
// shipped / delivered / billed, not closed or cancelled. "All open" moved to
// the end (Stephanie 2026-10-12). It replaces "With supplier" (same stages).
const STAGE_GROUPS: Array<{ value: StageGroup; label: string; stages?: string[] }> = [
  { value: "in_production", label: "In Production", stages: ["sent", "confirmed", "in_production"] },
  { value: "not_sent", label: "Not sent", stages: ["draft"] },
  { value: "shipping", label: "Shipped", stages: ["shipped", "delivered"] },
  { value: "billed", label: "Billed", stages: ["billed"] },
  { value: "closed", label: "Closed / cancelled", stages: ["closed", "cancelled"] },
  { value: "all", label: "All open" },
];

type SortKey = "urgent" | "next_action" | "in_hands" | "placed" | "newest";

// Default: next action date (blanks last), then in-hands date (Stephanie 2026-10-12).
const DEFAULT_SORT: SortKey = "next_action";

const SORTS: Array<{ value: SortKey; label: string }> = [
  { value: "next_action", label: "Next action date" },
  { value: "urgent", label: "Most urgent" },
  { value: "in_hands", label: "In-hands date" },
  { value: "placed", label: "Date placed" },
  { value: "newest", label: "Newest PO" },
];

const byDate = (k: "inHandsDate" | "nextActionDate" | "sentAt") => (a: ProductionJob, b: ProductionJob) =>
  (a[k] ?? "9999").localeCompare(b[k] ?? "9999");

const byNextAction = (a: ProductionJob, b: ProductionJob) => byDate("nextActionDate")(a, b) || byDate("inHandsDate")(a, b);

// Sized for reading at a glance and fitting the whole report on one screen
// (Stephanie 2026-10-12): 15px text, tighter cell padding than other tables,
// headings and cells centred.
const RTD = cn(TD, "px-2 py-3 text-center align-middle");
const RTH = cn(TH, "px-2 py-3 text-center align-bottom text-[13px] leading-tight");

const TONE = { ink: "text-ink", muted: "text-muted", warn: "text-warn" } as const;

/** Rush = inverted row; Firm = heavy black outline. Both can apply. */
function rowUrgencyClass(j: ProductionJob): string {
  return cn(
    j.rush && "bg-ink text-white hover:bg-ink",
    j.firmInHands && "outline outline-[3px] -outline-offset-[3px] outline-ink",
  );
}

function UrgencyTags({ j }: { j: ProductionJob }) {
  if (!j.rush && !j.firmInHands) return null;
  const tag = cn(
    "rounded-sm border px-1.5 py-px text-[10px] font-semibold uppercase tracking-[0.08em]",
    j.rush ? "border-white text-white" : "border-ink text-ink",
  );
  return (
    <span className="mt-1 flex flex-col items-center gap-1">
      {j.rush && <span className={tag}>Rush</span>}
      {j.firmInHands && <span className={tag}>Firm</span>}
    </span>
  );
}

/** "Unassigned" filter value for POs whose project has no rep of the chosen kind. */
const NO_REP = "__none";

const nextActionLabel = (t: string | null): string => (t ? NEXT_ACTION_LABEL[t] ?? t.replace(/_/g, " ") : "—");

/** Which rep the rep column shows — Order Rep by default, switchable to Client Rep. */
type RepKind = "order" | "client";
const REP_KINDS: Array<{ value: RepKind; label: string }> = [
  { value: "order", label: "Order" },
  { value: "client", label: "Client" },
];
const repName = (j: ProductionJob, kind: RepKind): string | null => (kind === "client" ? j.ownerName : j.orderRepName);

/** "Jordan Lee" → "JL" (to save space; the full name shows on hover). */
function initials(name: string | null): string {
  if (!name) return "";
  return name
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase())
    .join("");
}

/** "2026-10-07…" → "10/07/26" — every date in the report (Stephanie 2026-10-12: xx/xx/xx). */
function mmddyy(s: string | null): string {
  const m = s ? /^(\d{2})(\d{2})-(\d{2})-(\d{2})/.exec(s) : null;
  return m ? `${m[3]}/${m[4]}/${m[2]}` : "";
}

type ProductionStatus = NonNullable<ProductionJob["productionStatus"]>;
const STATUS_OPTIONS: Array<{ value: ProductionStatus; label: string; className: string }> = [
  { value: "ok", label: "OK", className: "bg-[#DCE5D5] text-[#3E5234] border-[#C3D1B8]" },
  { value: "follow_up_vendor", label: "F/U Vendor", className: "bg-[#D5E1EC] text-[#2F4A63] border-[#B9CCDD]" },
  { value: "follow_up_rep", label: "F/U Rep", className: "bg-[#DCC6DC] text-[#4A2A4A] border-[#C9AAC9]" },
  // Stephanie 2026-11-03. Pickable once Jake runs scripts/push-po-status-being-shipped.ts.
  { value: "being_shipped", label: "Being Shipped", className: "bg-[#F3E3C3] text-[#6B4E16] border-[#E3CC9C]" },
];
const statusOption = (v: ProductionStatus) => STATUS_OPTIONS.find((o) => o.value === v) ?? STATUS_OPTIONS[0]!;

function StatusCell({ j, canEdit }: { j: ProductionJob; canEdit: boolean }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (v: ProductionStatus | { status: ProductionStatus; note?: string }) => {
      const body = typeof v === "string" ? { status: v } : v;
      return api<{ status: ProductionStatus }>(`/pos/${j.id}/production-status`, { method: "PUT", body: JSON.stringify(body) });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(body?.error ?? "Not saved — try again.");
    },
  });
  if (!j.productionStatus) return <span className="opacity-70">—</span>;
  const pending = save.isPending && save.variables ? save.variables : null;
  const shown = pending ? (typeof pending === "string" ? pending : pending.status) : j.productionStatus;
  const opt = statusOption(shown);
  const pill = cn("rounded-full border py-1 pl-2.5 pr-1 text-[14px] font-medium", opt.className);
  if (!canEdit) return <span className={pill}>{opt.label}</span>;
  return (
    <>
      <select
        aria-label={`Status for PO #${j.poNumber}`}
        value={shown}
        disabled={save.isPending}
        onChange={(e) => {
          const next = e.target.value as ProductionStatus;
          // F/U Rep: say what's needed — it goes on the rep's dashboard and the
          // project's activity (Stephanie 2026-10-14). Optional.
          if (next === "follow_up_rep") {
            const note = window.prompt(`What do you need from the rep on PO #${j.poNumber}? (optional)`) ?? "";
            save.mutate({ status: next, ...(note.trim() ? { note: note.trim() } : {}) });
          } else {
            save.mutate(next);
          }
        }}
        className={cn(pill, "cursor-pointer")}
      >
        {STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value} className="bg-white text-ink">{o.label}</option>
        ))}
      </select>
      {error && <span role="alert" className="mt-1 block text-[11px] font-medium">{error}</span>}
    </>
  );
}

/** Date placed; click it to show the time the PO was placed underneath (Stephanie 2026-10-12). */
function PlacedCell({ sentAt }: { sentAt: string }) {
  const [open, setOpen] = useState(false);
  const time = new Date(sentAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return (
    <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="inline-flex flex-col items-center">
      <span className="inline-flex items-center gap-1">
        {mmddyy(sentAt)}
        <ChevronDown className={cn("h-3.5 w-3.5 opacity-60 transition-transform", open && "rotate-180")} aria-hidden />
      </span>
      {open && <span className="text-[12px] opacity-80">{time}</span>}
    </button>
  );
}

/**
 * Stage + Next action dropdowns (Stephanie 2026-10-12). Admin / production
 * reps change them right in the table. Picking a stage only records it — e.g.
 * "Sent" does NOT email the supplier (that stays the Send PO button on the
 * PO). Cancelled POs keep their stage. Everyone else sees plain text.
 */
const STAGE_OPTIONS = ["draft", "sent", "confirmed", "in_production", "shipped", "delivered", "billed", "closed"] as const;
const stageLabel = (s: string) => (s === "draft" ? "Not sent" : poStageLabel(s));
const SELECT = "h-10 max-w-[172px] rounded-md border border-rule bg-white pl-2 pr-1 text-[14px] text-ink disabled:opacity-60";

function useJobSave<T>(path: (j: ProductionJob) => string, method: "PUT" | "PATCH") {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: ({ j, body }: { j: ProductionJob; body: T }) => api(path(j), { method, body: JSON.stringify(body) }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
    },
    onError: (err) => {
      const b = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(b?.error ?? "Not saved — try again.");
    },
  });
  return { m, error };
}

function StageCell({ j, canEdit }: { j: ProductionJob; canEdit: boolean }) {
  const { m, error } = useJobSave<{ stage: string }>((x) => `/pos/${x.id}/stage`, "PUT");
  if (!canEdit || j.stage === "cancelled") return <span className={j.rush ? "text-white" : "text-ink"}>{stageLabel(j.stage)}</span>;
  const shown = m.isPending ? m.variables!.body.stage : j.stage;
  return (
    <>
      <select aria-label={`Stage for PO #${j.poNumber}`} value={shown} disabled={m.isPending} onChange={(e) => m.mutate({ j, body: { stage: e.target.value } })} className={SELECT}>
        {STAGE_OPTIONS.map((s) => (
          <option key={s} value={s}>{stageLabel(s)}</option>
        ))}
      </select>
      {error && <span role="alert" className="mt-1 block text-[11px] font-medium">{error}</span>}
    </>
  );
}

function NextActionCell({ j, canEdit }: { j: ProductionJob; canEdit: boolean }) {
  const { m, error } = useJobSave<{ next_action_type: string | null }>((x) => `/pos/${x.id}`, "PATCH");
  if (!canEdit) return <span className={j.rush ? "text-white" : j.nextActionType ? "text-ink" : "text-muted"}>{nextActionLabel(j.nextActionType)}</span>;
  const shown = m.isPending ? m.variables!.body.next_action_type ?? "" : j.nextActionType ?? "";
  return (
    <>
      <select
        aria-label={`Next action for PO #${j.poNumber}`}
        value={shown}
        disabled={m.isPending}
        onChange={(e) => m.mutate({ j, body: { next_action_type: e.target.value || null } })}
        className={SELECT}
      >
        <option value="">—</option>
        {Object.entries(NEXT_ACTION_LABEL).map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
      {error && <span role="alert" className="mt-1 block text-[11px] font-medium">{error}</span>}
    </>
  );
}

/** Next action date, picked right on the report (Stephanie 2026-10-16:
 *  "there is nowhere to select the next action date"). Saves when a whole
 *  date is picked, or when it's cleared. */
function NextActionDateCell({ j, canEdit, overdue }: { j: ProductionJob; canEdit: boolean; overdue: boolean }) {
  const { m, error } = useJobSave<{ next_action_date: string | null }>((x) => `/pos/${x.id}`, "PATCH");
  if (!canEdit) return <>{mmddyy(j.nextActionDate) || "—"}</>;
  const shown = m.isPending ? m.variables!.body.next_action_date ?? "" : (j.nextActionDate ?? "").slice(0, 10);
  // The box READS as MM/DD/YY to save space (Stephanie 2026-10-16); the
  // browser's own date picker sits invisibly on top, so a click opens it.
  return (
    <>
      <span className={cn(SELECT, "relative inline-flex w-[112px] items-center px-2", overdue && "border-warn font-semibold text-warn", !shown && "text-muted")}>
        {shown ? mmddyy(shown) : "mm/dd/yy"}
        <input
          type="date"
          aria-label={`Next action date for PO #${j.poNumber}`}
          value={shown}
          disabled={m.isPending}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "" || /^\d{4}-\d{2}-\d{2}$/.test(v)) m.mutate({ j, body: { next_action_date: v || null } });
          }}
          onClick={(e) => (e.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.()}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </span>
      {error && <span role="alert" className="mt-1 block text-[11px] font-medium">{error}</span>}
    </>
  );
}

/**
 * "Proof sent" (Stephanie 2026-10-12): ticked once a proof is on the PO. Ticking
 * it opens a pop-up to pick the item and upload the proof; sending saves it on
 * the PO and alerts + emails the order rep and the client rep (PO #, client,
 * item, proof attached). Ticking a ticked box sends a revised proof.
 */
function ProofSentCell({ j, canSend, onOpen }: { j: ProductionJob; canSend: boolean; onOpen: () => void }) {
  const sent = j.proofs.total > 0;
  // No box when every logo says "No proof required" (Stephanie 2026-10-28) —
  // unless a proof was sent anyway, which still shows.
  if (j.proofNeeded === false && !sent) {
    return <span className="text-sm text-muted" title="Every logo on this PO is marked No proof required">Not needed</span>;
  }
  return (
    <input
      type="checkbox"
      aria-label={sent ? `Proof sent for PO #${j.poNumber} — send a revised proof` : `Send a proof for PO #${j.poNumber}`}
      checked={sent}
      disabled={!canSend}
      onChange={(e) => {
        e.preventDefault();
        onOpen();
      }}
      className="h-5 w-5 cursor-pointer accent-ink disabled:cursor-default"
    />
  );
}

interface ProofSentResponse {
  reps: number;
  alertsSaved: number;
  alertsPushed: number;
  emailsSent: number;
  emailErrors: string[];
}

/** Notes on each order (Stephanie 2026-10-16): a small button with how many
 *  notes there are — the notes themselves only show in the popup ("I don't
 *  want the notes showing all of the time"). Same notes as the PO page. */
function NotesCell({ j, onOpen }: { j: ProductionJob; onOpen: () => void }) {
  const n = j.notes ?? { count: 0, latest: null };
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Notes for PO #${j.poNumber}`}
      className={cn(
        "inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-[14px] font-medium",
        j.rush
          ? "border-white/60 text-white hover:bg-white hover:text-ink"
          : n.count > 0
            ? "border-ink text-ink hover:bg-canvas"
            : "border-rule text-muted hover:text-ink",
      )}
    >
      <StickyNote className="h-4 w-4" aria-hidden />
      {n.count > 0 ? `Notes (${n.count})` : "Add note"}
    </button>
  );
}

function NotesDialog({ j, onClose }: { j: ProductionJob; onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  // "@" tags (Stephanie 2026-10-21): they get the bell + an email.
  const [mentions, setMentions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const key = ["pos", j.id, "notes"];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<{ notes: Array<{ id: string; body: string; created_at: string | null; by: string | null }> }>(`/pos/${j.id}/notes`),
  });
  const add = useMutation({
    mutationFn: (body: string) => api(`/pos/${j.id}/notes`, { method: "POST", body: JSON.stringify({ body, mention_user_ids: mentions }) }),
    onSuccess: () => {
      setText("");
      setMentions([]);
      setError(null);
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
      void qc.invalidateQueries({ queryKey: ["pos", j.id, "detail"] });
    },
    onError: () => setError("Not saved — try again."),
  });
  const when = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit", hour: "numeric", minute: "2-digit" }) : "";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" role="dialog" aria-modal="true" aria-label={`Notes — PO #${j.poNumber}`}>
      <div className="flex max-h-[85vh] w-full max-w-xl flex-col rounded-xl bg-white text-ink shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-rule px-6 py-4">
          <div>
            <p className="text-lg font-semibold">Notes — PO #{j.poNumber}</p>
            <p className="text-sm text-muted">{j.vendorName} · {j.clientName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-canvas">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        <div className="space-y-3 border-b border-rule px-6 py-4">
          <MentionTextarea
            value={text}
            onChange={(t, ids) => { setText(t); setMentions(ids); }}
            rows={3}
            maxLength={4000}
            placeholder="Add a note for this order… type @ to tag someone"
            aria-label="New note"
            className="w-full rounded-md border border-rule bg-white px-3 py-2 text-[17px]"
          />
          {error && <p role="alert" className="text-sm text-risk">{error}</p>}
          <div className="flex justify-end">
            <Button onClick={() => add.mutate(text.trim())} disabled={!text.trim() || add.isPending}>
              {add.isPending ? "Saving…" : "Add note"}
            </Button>
          </div>
        </div>
        <div className="flex-1 divide-y divide-rule overflow-y-auto px-6 text-[17px] leading-relaxed">
          {q.isLoading ? (
            <p className="py-5 text-muted">Loading notes…</p>
          ) : (q.data?.notes ?? []).length === 0 ? (
            <p className="py-5 text-muted">No notes yet.</p>
          ) : (
            q.data!.notes.map((n) => (
              <div key={n.id} className="py-4">
                <p className="text-[13px] font-semibold text-muted">{[n.by, when(n.created_at)].filter(Boolean).join(" · ")}</p>
                <p className="mt-1 whitespace-pre-wrap text-ink"><NoteText text={n.body} /></p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ProofSentDialog({ j, onClose }: { j: ProductionJob; onClose: () => void }) {
  const qc = useQueryClient();
  const itemsQ = useQuery({
    queryKey: ["pos", j.id, "proof-items"],
    queryFn: () => api<{ items: Array<{ lineItemId: string; name: string; quantity: number }> }>(`/pos/${j.id}/proof-items`),
  });
  const items = itemsQ.data?.items ?? [];
  const [itemId, setItemId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ProofSentResponse | null>(null);
  const chosen = itemId || (items.length === 1 ? items[0]!.lineItemId : "");

  const send = useMutation({
    mutationFn: async () => {
      if (!file || !chosen) throw new Error("Pick the item and attach the proof.");
      if (file.size > 20 * 1024 * 1024) throw new Error("That proof is over 20 MB, too big to email. Please send a smaller file.");
      const presign = await api<{ file_id: string; upload_url: string; live: boolean }>("/files/presign", {
        method: "POST",
        body: JSON.stringify({
          filename: file.name,
          mime_type: file.type || "application/octet-stream",
          size_bytes: file.size,
          file_purpose: "proof",
          visibility: "internal",
          prefix: `pos/${j.id}/proofs`,
        }),
      });
      if (presign.live) {
        const put = await fetch(presign.upload_url, { method: "PUT", headers: { "Content-Type": file.type || "application/octet-stream" }, body: file });
        if (!put.ok) throw new Error("The upload didn't go through. Try again.");
      }
      return api<ProofSentResponse>(`/pos/${j.id}/proof-sent`, {
        method: "POST",
        body: JSON.stringify({ file_id: presign.file_id, line_item_id: chosen }),
      });
    },
    onSuccess: (r) => {
      setDone(r);
      void qc.invalidateQueries({ queryKey: ["production", "jobs"] });
    },
    onError: (err) => {
      const body = err instanceof ApiError ? (err.body as { error?: string } | null) : null;
      setError(body?.error ?? (err instanceof Error ? err.message : "We couldn't send the proof. Try again."));
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" role="dialog" aria-modal="true" aria-label={`Proof sent — PO #${j.poNumber}`}>
      <div className="w-full max-w-lg rounded-xl bg-white text-ink shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-rule px-6 py-4">
          <div>
            <p className="text-lg font-semibold">Proof sent — PO #{j.poNumber}</p>
            <p className="text-sm text-muted">{j.clientName}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-canvas">
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        {done ? (
          <div className="space-y-3 px-6 py-5 text-sm">
            <p className="font-medium">Proof sent.</p>
            <p className="text-muted">
              {done.reps === 0
                ? "This project has no order rep or client rep, so nobody was alerted. The proof is saved on the PO."
                : `${done.emailsSent} of ${done.reps} rep${done.reps === 1 ? "" : "s"} emailed with the proof attached, and alerted in Flow.`}
            </p>
            {done.emailErrors.length > 0 && <p className="text-risk">The email to {done.emailErrors.join(", ")} didn't go through.</p>}
            <div className="flex justify-end pt-2">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 px-6 py-5">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Which item is this proof for?</span>
              {itemsQ.isLoading ? (
                <span className="block h-10 animate-pulse rounded-md bg-canvas" />
              ) : items.length === 0 ? (
                <span className="block text-muted">This PO has no items yet.</span>
              ) : (
                <select value={chosen} onChange={(e) => setItemId(e.target.value)} className="h-10 w-full rounded-md border border-rule bg-white px-3 text-sm">
                  {items.length > 1 && <option value="">Choose an item…</option>}
                  {items.map((i) => (
                    <option key={i.lineItemId} value={i.lineItemId}>
                      {i.name} (qty {i.quantity.toLocaleString()})
                    </option>
                  ))}
                </select>
              )}
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">Proof file</span>
              <input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.ai,.eps,.svg"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setFile(f);
                  setError(f && f.size > 20 * 1024 * 1024 ? "That proof is over 20 MB, too big to email. Please send a smaller file." : null);
                }}
                className="block w-full text-sm file:mr-3 file:rounded-md file:border file:border-rule file:bg-white file:px-3 file:py-2 file:text-sm file:text-ink hover:file:bg-canvas"
              />
            </label>
            <p className="text-[13px] text-muted">
              The order rep and client rep get a Flow alert and an email with the proof attached, showing the PO #, client and item.
              {j.proofs.total > 0 && " If this item already had a proof, they'll be told this is a revised proof."}
            </p>
            {error && <p role="alert" className="text-sm text-risk">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={() => { setError(null); send.mutate(); }} disabled={!file || !chosen || file.size > 20 * 1024 * 1024 || send.isPending}>
                {send.isPending ? "Sending…" : "Send proof"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** The report's columns, in Stephanie's order — shared by the table and the CSV (Stephanie 2026-10-12). */
function reportColumns(kind: RepKind, withStatus: boolean): Array<{ key: string; label: string; value: (j: ProductionJob) => string }> {
  return [
    { key: "supplier", label: "Supplier", value: (j) => j.vendorName },
    { key: "po", label: "PO #", value: (j) => String(j.poNumber) },
    { key: "placed", label: "Date placed", value: (j) => mmddyy(j.sentAt) },
    ...(withStatus ? [{ key: "status", label: "Status", value: (j: ProductionJob) => (j.productionStatus ? statusOption(j.productionStatus).label : "") }] : []),
    { key: "stage", label: "Stage", value: (j) => stageLabel(j.stage) },
    { key: "proof", label: "Proof sent", value: (j) => (j.proofs.total > 0 ? "Yes" : j.proofNeeded === false ? "Not needed" : "") },
    { key: "client", label: "Client", value: (j) => j.clientName },
    { key: "rep", label: kind === "client" ? "Client Rep" : "Order Rep", value: (j) => repName(j, kind) ?? "" },
    { key: "next_type", label: "Next action type", value: (j) => (j.nextActionType ? nextActionLabel(j.nextActionType) : "") },
    { key: "next_date", label: "Next action date", value: (j) => mmddyy(j.nextActionDate) },
    // The PO's Ship by date, after Next action date (Stephanie 2026-11-03).
    { key: "ship_date", label: "Ship date", value: (j) => mmddyy(j.requiredShipDate) },
    { key: "in_hands", label: "In hands", value: (j) => mmddyy(j.inHandsDate) },
    { key: "notes", label: "Notes", value: (j) => j.notes?.latest?.body ?? "" },
  ];
}

/**
 * Click a column heading to sort by it, A→Z (oldest / lowest first); click
 * again for Z→A (Stephanie 2026-10-12). Blanks always go last. Saved in the
 * URL (?col=&dir=) like the other filters; picking from the Sort menu clears it.
 */
type SortVal = string | number | null;
const STATUS_ORDER: Record<string, number> = { ok: 0, follow_up_vendor: 1, follow_up_rep: 2, being_shipped: 3 };
const STAGE_ORDER: Record<string, number> = Object.fromEntries(STAGE_OPTIONS.map((s, i) => [s, i]));
function columnSortValue(key: string, j: ProductionJob, kind: RepKind): SortVal {
  switch (key) {
    case "supplier": return j.vendorName.toLowerCase();
    case "po": return j.poNumber;
    case "placed": return j.sentAt;
    case "status": return j.productionStatus ? STATUS_ORDER[j.productionStatus] ?? 9 : null;
    case "stage": return STAGE_ORDER[j.stage] ?? 99;
    case "proof": return j.proofs.total > 0 ? 0 : 1;
    case "client": return j.clientName.toLowerCase();
    case "rep": return repName(j, kind)?.toLowerCase() ?? null;
    case "next_type": return j.nextActionType ? nextActionLabel(j.nextActionType).toLowerCase() : null;
    case "next_date": return j.nextActionDate;
    case "ship_date": return j.requiredShipDate;
    case "in_hands": return j.inHandsDate;
    case "notes": return j.notes?.latest?.body.toLowerCase() ?? null;
    default: return null;
  }
}
function byColumn(key: string, dir: "asc" | "desc", kind: RepKind) {
  return (a: ProductionJob, b: ProductionJob): number => {
    const x = columnSortValue(key, a, kind);
    const y = columnSortValue(key, b, kind);
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
    const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true });
    return dir === "asc" ? c : -c;
  };
}

/** Extra CSV columns (not on screen as columns — shown as row highlights). */
const CSV_EXTRA: Array<{ label: string; value: (j: ProductionJob) => string }> = [
  { label: "Rush", value: (j) => (j.rush ? "Yes" : "") },
  { label: "Firm in-hands", value: (j) => (j.firmInHands ? "Yes" : "") },
];

// csvCell (lib/csv) also defuses spreadsheet formulas (security audit 2026-10-21, M5).

function reportCsv(rows: ProductionJob[], kind: RepKind, withStatus: boolean): string {
  const cols = [...reportColumns(kind, withStatus), ...CSV_EXTRA];
  const lines = [cols.map((c) => csvCell(c.label)).join(",")];
  for (const j of rows) lines.push(cols.map((c) => csvCell(c.value(j))).join(","));
  return lines.join("\r\n") + "\r\n";
}

function downloadCsv(rows: ProductionJob[], kind: RepKind, withStatus: boolean) {
  const blob = new Blob([reportCsv(rows, kind, withStatus)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `production-report-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function ProductionReportPage() {
  const [params, setParams] = useSearchParams();
  // Default tab is In Production; a link that only names a problem (e.g. the
  // dashboard's "Late POs" → ?blocker=late) opens on All open so nothing is
  // hidden. Old "with_supplier" links land on In Production.
  const rawStage = params.get("stage");
  const stage: StageGroup =
    rawStage === "with_supplier" ? "in_production" : ((rawStage as StageGroup | null) ?? (params.get("blocker") ? "all" : "in_production"));
  const blocker = (params.get("blocker") as JobBlocker | null) ?? null;
  const repKind: RepKind = params.get("reps") === "client" ? "client" : "order";
  const rep = params.get("rep") ?? "";
  const q = params.get("q") ?? "";
  const sort = (params.get("sort") as SortKey | null) ?? DEFAULT_SORT;
  const col = params.get("col");
  const dir: "asc" | "desc" = params.get("dir") === "desc" ? "desc" : "asc";
  const includeClosed = stage === "closed";

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "" || (key === "stage" && value === "in_production" && !params.get("blocker")) || (key === "sort" && value === DEFAULT_SORT) || (key === "reps" && value === "order")) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  const jobsQ = useProductionJobs(includeClosed ? "all" : "open");
  const all = jobsQ.data?.jobs ?? [];

  const rows = useMemo(() => {
    const group = STAGE_GROUPS.find((g) => g.value === stage);
    const term = q.trim().toLowerCase();
    const filtered = all.filter((j) => {
      if (group?.stages && !group.stages.includes(j.stage)) return false;
      if (!group?.stages && (j.stage === "closed" || j.stage === "cancelled")) return false;
      if (blocker && !j.blockers.includes(blocker)) return false;
      if (rep && (rep === NO_REP ? repName(j, repKind) !== null : repName(j, repKind) !== rep)) return false;
      if (term) {
        const hay = `${j.poNumber} ${j.orderNumber} ${j.vendorName} ${j.shipFromVendorName ?? ""} ${j.clientName} ${j.projectName} ${j.ownerName ?? ""} ${j.orderRepName ?? ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
    const cmp = col
      ? byColumn(col, dir, repKind)
      : sort === "urgent"
        ? sortByUrgency
        : sort === "in_hands"
          ? byDate("inHandsDate")
          : sort === "placed"
            ? byDate("sentAt")
            : sort === "newest"
              ? (a: ProductionJob, b: ProductionJob) => b.poNumber - a.poNumber
              : byNextAction;
    // Rush orders are pinned to the top whatever the sort (Stephanie
    // 2026-10-14); within rush and non-rush rows the chosen sort applies.
    return [...filtered].sort((a, b) => Number(b.rush) - Number(a.rush) || cmp(a, b));
  }, [all, stage, blocker, rep, repKind, q, sort, col, dir]);

  // Heading click: first click A→Z, next click Z→A, and so on.
  const sortBy = (key: string) => {
    const next = new URLSearchParams(params);
    next.set("col", key);
    next.set("dir", col === key && dir === "asc" ? "desc" : "asc");
    setParams(next, { replace: true });
  };
  const SortHead = ({ k, label }: { k: string; label: string }) => (
    <button
      type="button"
      onClick={() => sortBy(k)}
      className="inline-flex items-center justify-center gap-0.5 font-medium hover:text-ink"
      aria-label={`Sort by ${label}${col === k ? (dir === "asc" ? ", A to Z" : ", Z to A") : ""}`}
    >
      {label}
      <span aria-hidden className={cn("text-[11px]", col === k ? "text-ink" : "opacity-30")}>
        {col === k && dir === "desc" ? "▼" : "▲"}
      </span>
    </button>
  );

  const reps = useMemo(
    () => [...new Set(all.map((j) => repName(j, repKind)).filter((n): n is string => !!n))].sort((a, b) => a.localeCompare(b)),
    [all, repKind],
  );
  // Status shows once the database has the column (every job then carries one).
  const withStatus = all.some((j) => j.productionStatus !== null);
  const columns = reportColumns(repKind, withStatus);
  const { user } = useAuth();
  const canEditStatus = !!user && (user.roles.includes("admin") || user.roles.includes("production_rep"));
  const [proofFor, setProofFor] = useState<ProductionJob | null>(null);
  const [notesFor, setNotesFor] = useState<ProductionJob | null>(null);
  const repLabel = repKind === "client" ? "client rep" : "order rep";
  // Switching Order Rep ↔ Client Rep clears a rep filter (it named the other kind).
  const setRepKind = (k: RepKind) => {
    const next = new URLSearchParams(params);
    next.delete("rep");
    if (k === "order") next.delete("reps");
    else next.set("reps", k);
    setParams(next, { replace: true });
  };

  const open = all.filter((j) => j.stage !== "closed" && j.stage !== "cancelled");
  const count = (b: JobBlocker) => (jobsQ.data ? open.filter((j) => j.blockers.includes(b)).length : null);
  const hasFilters = stage !== "in_production" || blocker !== null || rep !== "" || q !== "";

  return (
    <div className="w-full">
      <PageHeader
        eyebrow="Production / Production Report"
        title="Production Report"
        intro="Every purchase order in production with its client, rep, dates and next action. Filter it down to what needs you, or export it."
      />

      <div className="mb-7">
        <MetricStrip
          ariaLabel="Production report summary"
          cells={[
            { label: "Open purchase orders", value: jobsQ.data ? open.length : null, note: "Not closed or cancelled", onClick: () => setParams(new URLSearchParams({ stage: "all" }), { replace: true }) },
            { label: "Past ship-by date", value: count("late"), note: "Still with the supplier", attention: (count("late") ?? 0) > 0, onClick: () => set("blocker", "late") },
            { label: "Blanks not arrived", value: count("blanks_not_arrived"), note: "Decorator waiting on goods", attention: (count("blanks_not_arrived") ?? 0) > 0, onClick: () => set("blocker", "blanks_not_arrived") },
            { label: "Proofs open", value: jobsQ.data ? open.filter((j) => j.proofs.withUs + j.proofs.withClient > 0).length : null, note: "With us or the client" },
          ]}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-rule">
        <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <div className="min-w-max">
            <FilterTabs
              ariaLabel="Filter by stage"
              options={STAGE_GROUPS.map((g) => ({ value: g.value, label: g.label }))}
              value={stage}
              onChange={(v) => set("stage", v)}
            />
          </div>
        </div>
      </div>
      <div className="mb-4 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden />
          <Input
            placeholder="PO, order, supplier, client, project or rep"
            aria-label="Search the production report"
            value={q}
            onChange={(e) => set("q", e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          aria-label="Filter by what's blocking"
          value={blocker ?? ""}
          onChange={(e) => set("blocker", e.target.value || null)}
          className="h-10 rounded-md border border-rule bg-white px-3 text-sm text-ink"
        >
          <option value="">Anything blocking</option>
          {BLOCKER_ORDER.map((b) => (
            <option key={b} value={b}>{BLOCKER_COPY[b].label}</option>
          ))}
        </select>
        <select
          aria-label={`Filter by ${repLabel}`}
          value={rep}
          onChange={(e) => set("rep", e.target.value || null)}
          className="h-10 rounded-md border border-rule bg-white px-3 text-sm text-ink"
        >
          <option value="">Any {repLabel}</option>
          {reps.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
          <option value={NO_REP}>No {repLabel}</option>
        </select>
        <select
          aria-label="Sort"
          value={sort}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            next.delete("col");
            next.delete("dir");
            if (e.target.value === DEFAULT_SORT) next.delete("sort");
            else next.set("sort", e.target.value);
            setParams(next, { replace: true });
          }}
          className="h-10 rounded-md border border-rule bg-white px-3 text-sm text-ink"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <div className={TABLE_WRAP} role="region" aria-label="Production records" tabIndex={0}>
        {jobsQ.isLoading ? (
          <div className="space-y-3 p-5" aria-label="Loading production records">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
            ))}
          </div>
        ) : jobsQ.isError ? (
          <div className="p-5 text-sm text-risk" role="alert">
            We couldn't load production records. Nothing has changed — refresh to try again.
          </div>
        ) : rows.length === 0 ? (
          <div className="px-5 py-12 text-center">
            {hasFilters ? (
              <>
                <p className="font-medium text-ink">No purchase orders match.</p>
                <button type="button" onClick={() => setParams(new URLSearchParams(), { replace: true })} className="mt-2 min-h-[40px] text-sm text-ink underline underline-offset-4">
                  Clear filters
                </button>
              </>
            ) : (
              <>
                <p className="font-medium text-ink">No purchase orders in production.</p>
                <p className="mt-1 text-sm text-muted">Purchase orders appear here once a quote is handed to production.</p>
              </>
            )}
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                {columns.map((c) =>
                  c.key === "rep" ? (
                    <th key={c.key} scope="col" className={RTH}>
                      {/* The rep column can show the Order Rep (default) or the Client Rep. */}
                      <SortHead k="rep" label="Rep" />
                      <select
                        aria-label="Rep column shows"
                        value={repKind}
                        onChange={(e) => setRepKind(e.target.value as RepKind)}
                        className="mx-auto mt-0.5 block rounded border border-rule bg-white py-0.5 pl-1 pr-4 text-[12px] font-medium text-ink"
                      >
                        {REP_KINDS.map((k) => (
                          <option key={k.value} value={k.value}>{k.label}</option>
                        ))}
                      </select>
                    </th>
                  ) : (
                    <th key={c.key} scope="col" className={RTH} aria-sort={col === c.key ? (dir === "asc" ? "ascending" : "descending") : undefined}>
                      <SortHead k={c.key} label={c.label} />
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="text-[15px] leading-snug">
              {rows.map((j) => {
                const ih = daysFromToday(j.inHandsDate);
                const na = daysFromToday(j.nextActionDate);
                const rn = repName(j, repKind);
                const done = ["shipped", "delivered", "billed", "closed", "cancelled"].includes(j.stage);
                // On an inverted (rush) row every cell is white; overdue goes bold instead of orange.
                const tone = (kind: "ink" | "muted" | "warn") =>
                  j.rush ? cn("text-white", kind === "muted" && "opacity-70", kind === "warn" && "font-semibold") : TONE[kind];
                return (
                  <tr key={j.id} className={cn(TROW, rowUrgencyClass(j))}>
                    <td className={cn(RTD, tone("ink"))}>{j.vendorName}</td>
                    <td className={cn(RTD, "whitespace-nowrap")}>
                      <Link to={poHref(j)} className={cn("font-medium hover:underline", tone("ink"))} aria-label={`PO #${j.poNumber}`}>#{j.poNumber}</Link>
                      <UrgencyTags j={j} />
                    </td>
                    <td className={cn(RTD, "whitespace-nowrap", tone(j.sentAt ? "ink" : "muted"))}>
                      {j.sentAt ? <PlacedCell sentAt={j.sentAt} /> : "Not sent"}
                    </td>
                    {withStatus && (
                      <td className={cn(RTD, "whitespace-nowrap")}>
                        <StatusCell j={j} canEdit={canEditStatus} />
                      </td>
                    )}
                    <td className={cn(RTD, "whitespace-nowrap")}>
                      <StageCell j={j} canEdit={canEditStatus} />
                    </td>
                    <td className={cn(RTD, "text-center")}>
                      <ProofSentCell j={j} canSend={canEditStatus} onOpen={() => setProofFor(j)} />
                    </td>
                    <td className={cn(RTD, tone("ink"))}>{j.clientName}</td>
                    <td className={cn(RTD, "whitespace-nowrap", tone(rn ? "ink" : "muted"))} title={rn ?? undefined}>
                      {rn ? initials(rn) : "—"}
                    </td>
                    <td className={cn(RTD, "whitespace-nowrap")}>
                      <NextActionCell j={j} canEdit={canEditStatus} />
                    </td>
                    <td className={cn(RTD, "whitespace-nowrap", tone(na !== null && na < 0 && !done ? "warn" : "ink"))}>
                      <NextActionDateCell j={j} canEdit={canEditStatus} overdue={na !== null && na < 0 && !done} />
                    </td>
                    <td className={cn(RTD, "whitespace-nowrap", tone(j.requiredShipDate ? "ink" : "muted"))}>
                      {mmddyy(j.requiredShipDate) || "—"}
                    </td>
                    <td className={cn(RTD, "whitespace-nowrap", tone(ih !== null && ih < 0 && !done ? "warn" : "ink"))}>
                      {mmddyy(j.inHandsDate) || "—"}
                    </td>
                    <td className={RTD}>
                      <NotesCell j={j} onOpen={() => setNotesFor(j)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted">
          Showing {rows.length} of {stage === "closed" ? all.filter((j) => j.stage === "closed" || j.stage === "cancelled").length : open.length} purchase orders
        </p>
        <button
          type="button"
          onClick={() => downloadCsv(rows, repKind, withStatus)}
          disabled={rows.length === 0}
          className="inline-flex min-h-[40px] items-center gap-2 rounded-md border border-rule bg-white px-3 text-sm text-ink hover:bg-canvas disabled:opacity-50"
        >
          <Download className="h-4 w-4" aria-hidden />
          Export CSV
        </button>
      </div>
      {proofFor && <ProofSentDialog j={proofFor} onClose={() => setProofFor(null)} />}
      {notesFor && <NotesDialog j={notesFor} onClose={() => setNotesFor(null)} />}
      <FreshnessNote asOf={jobsQ.data?.asOf} updatedAt={jobsQ.dataUpdatedAt} truncated={jobsQ.data?.truncated} />
    </div>
  );
}
